import { useState } from 'react';
import type { ProductRecommendations, ProductItem } from '../../types/diagnosis';

interface ProductTabsProps {
  products: ProductRecommendations;
  problemNames?: string[];
  className?: string;
}

type TabValue = 'budget' | 'premium';

/**
 * Editorial tabbed product recommendations
 */
export function ProductTabs({ products, problemNames = [], className = '' }: ProductTabsProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('budget');

  const handleKeyDown = (e: React.KeyboardEvent, tab: TabValue) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      setActiveTab(tab === 'budget' ? 'premium' : 'budget');
    }
  };

  return (
    <div className={className}>
      {/* Tab list */}
      <div
        role="tablist"
        aria-label="Product recommendation tiers"
        className="flex border-b border-gray-200 mb-4"
      >
        <button
          role="tab"
          id="tab-budget"
          aria-selected={activeTab === 'budget'}
          aria-controls="panel-budget"
          tabIndex={activeTab === 'budget' ? 0 : -1}
          onClick={() => setActiveTab('budget')}
          onKeyDown={(e) => handleKeyDown(e, 'budget')}
          className={`flex-1 min-h-[44px] px-4 py-2 text-xs font-bold tracking-widest uppercase transition-colors border-b-2 -mb-px ${
            activeTab === 'budget'
              ? 'border-editorial-black text-editorial-black'
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          Budget
        </button>
        <button
          role="tab"
          id="tab-premium"
          aria-selected={activeTab === 'premium'}
          aria-controls="panel-premium"
          tabIndex={activeTab === 'premium' ? 0 : -1}
          onClick={() => setActiveTab('premium')}
          onKeyDown={(e) => handleKeyDown(e, 'premium')}
          className={`flex-1 min-h-[44px] px-4 py-2 text-xs font-bold tracking-widest uppercase transition-colors border-b-2 -mb-px ${
            activeTab === 'premium'
              ? 'border-editorial-black text-editorial-black'
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          Premium
        </button>
      </div>

      {/* Budget panel */}
      <div
        role="tabpanel"
        id="panel-budget"
        aria-labelledby="tab-budget"
        hidden={activeTab !== 'budget'}
        className="space-y-3"
      >
        <p className="text-sm font-serif text-gray-500">{products.budget.description}</p>
        <ProductList items={products.budget.items} problemNames={problemNames} />
      </div>

      {/* Premium panel */}
      <div
        role="tabpanel"
        id="panel-premium"
        aria-labelledby="tab-premium"
        hidden={activeTab !== 'premium'}
        className="space-y-3"
      >
        <p className="text-sm font-serif text-gray-500">{products.premium.description}</p>
        <ProductList items={products.premium.items} problemNames={problemNames} />
      </div>
    </div>
  );
}

interface ProductListProps {
  items: ProductItem[];
  problemNames: string[];
}

function ProductList({ items, problemNames }: ProductListProps) {
  const showTags = problemNames.length > 1;

  if (items.length === 0) {
    return <p className="text-sm font-serif italic text-gray-400">No products recommended</p>;
  }

  return (
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li
          key={index}
          className="flex flex-col gap-1 p-3 border border-gray-200 rounded-lg bg-white"
        >
          <span className="text-editorial-black font-sans font-medium text-sm">{item.name}</span>
          {showTags && item.addressesProblems.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.addressesProblems.map((problem, i) => (
                <span
                  key={i}
                  className="inline-block px-2 py-0.5 text-[10px] font-sans font-bold tracking-wider uppercase bg-gray-100 text-gray-600 rounded-full"
                >
                  {problem}
                </span>
              ))}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
